import { definePreset, dt } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * Application theme preset built on top of Aura.
 *
 * Token hierarchy:
 *   primitive  → raw values (colors, sizes)
 *   semantic   → mapped roles (primary, formField, etc.) that reference primitives
 *   components → per-component overrides that reference semantic tokens
 *
 * CSS variable names map directly to camelCase keys, e.g.:
 *   --p-select-padding-y  →  components.select.paddingY
 *   --p-inputtext-padding-y  →  components.inputtext.paddingY
 */
export const AppPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '6px',
      lg: '8px',
      xl: '12px',
    },
  },

  semantic: {
    // Map --p-primary-* variables to a blue palette matching #1f88ca
    primary: {
      50: '#e8f4fc',
      100: '#c6e3f7',
      200: '#9fd0f1',
      300: '#78bcea',
      400: '#57ace5',
      500: '#1f88ca', // brand primary
      600: '#1878b5',
      700: '#12659c',
      800: '#0c5283',
      900: '#073f6a',
      950: '#042d51',
    },
    colorScheme: {
      light: {
        formField: {
          background: '{surface.100}',
          disabledBackground: '#ececec',
          borderColor: '{surface.300}',
          hoverBorderColor: 'transparent',
          focusBorderColor: '{primary.500}',
          invalidBorderColor: '{red.500}',
          color: '{surface.700}',
          placeholderColor: '{surface.400}',
          borderRadius: '{border.radius.lg}', // 8px
          paddingY: '0.75rem',
          paddingX: '1rem',
          shadow: 'none',
        },
      },
      dark: {
        formField: {
          background: '{surface.800}',
          disabledBackground: '{surface.700}',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          focusBorderColor: '{primary.400}',
        },
      },
    },
  },

  components: {
    // Override individual component tokens here as needed.
    // These generate --p-<component>-<token> CSS variables.
    // All values below are already inherited from semantic.formField above;
    // uncomment and adjust only what you need to diverge from that baseline.

    // select: {
    //   paddingY: '0.5rem',
    //   paddingX: '0.75rem',
    //   borderRadius: '{border.radius.lg}',
    // },

    // datepicker: {
    //   paddingY: '0.5rem',
    //   paddingX: '0.75rem',
    // },

    // inputtext: {
    //   paddingY: '0.75rem',
    //   paddingX: '1rem',
    // },

    // inputnumber: {
    //   paddingY: '0.75rem',
    //   paddingX: '1rem',
    // },

    button: {
      root: {
        paddingX: '2rem',
        paddingY: '0.825rem',
        sm: {
          paddingX: '1.75rem',
          paddingY: '0.7389375rem',
        },
        lg: {
          iconOnlyWidth: '2.5rem',
          fontSize: '1.375rem',
        },
      },
    },
    dialog: {
      content: {
        padding: '0.5rem 1.5rem 110px 1.5rem',
      },
      title: {
        fontWeight: 'normal',
        fontSize: '1.5rem',
      },
    },
    autoComplete: {
      dropdown: {
        background: '{primary.500}',
        color: '{primary.50}',
      },
      css: ({ dt }) => `
        .p-autocomplete-dropdown {
          background: ${dt('autocomplete.dropdown.background')} !important;
          color: ${dt('autocomplete.dropdown.color')} !important;
        }
      `,
    },
    datatable: {
      headerCell: {
        background: '{surface.100}',
      },
      header: {
        background: '{surface.100}',
      },
      css: ({ dt }) => `
        .p-datatable.p-datatable-sm .p-datatable-header {
        font-weight: 600;
        }
      `,
    },
    datepicker: {
      css: ({ dt }) => `
      .p-datepicker-weekday {
      font-weight: bold;
      }
      `,
    },

    // card: {
    //   borderRadius: '{border.radius.lg}',
    // },
    toast: {
      error: {
        detailColor: '#ffffff',
      },
    },
  },
});
